export interface User {
  id: string
  email: string
  name: string
  is_admin: boolean
  is_active: boolean
  first_login: boolean
  created_at: string
  updated_at: string
  last_login: string | null
}

export interface UserBasic {
  id: string
  email: string
  name: string
}

export interface UserStatistics {
  user_id: string
  username: string
  email: string
  images_annotated: number
  total_keypoints: number
  completed_projects: number
  rank: number
}

export interface StatisticsResponse {
  user_statistics: UserStatistics[]
}

// Message types
export interface ProjectMessage {
  id: string
  project_id: string
  sender_id: string
  recipient_id: string
  image_id?: string
  message: string
  message_type: string
  is_read: boolean
  read_at?: string
  created_at: string
  updated_at: string
}

export interface ProjectMessageWithDetails extends ProjectMessage {
  sender_name: string
  sender_email: string
  recipient_name: string
  recipient_email: string
  image_filename?: string
}

export interface ProjectMessageCreate {
  recipient_id: string
  message: string
  message_type?: string
  image_id?: string
}

export interface ConversationSummary {
  participant_id: string
  participant_name: string
  participant_email: string
  last_message: string
  last_message_at: string
  unread_count: number
  total_count: number
}

export interface ProjectMessageStats {
  total_messages: number
  unread_messages: number
  conversations: number
}

export enum VideoStatus {
  NONE = 'none',
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum ProjectStatus {
  NOT_STARTED = 'not_started',
  DRAFT = 'draft',
  NEEDS_REVISION = 'needs_revision',
  QA_PENDING = 'qa_pending',
  QA_APPROVED = 'qa_approved',
  ARCHIVED = 'archived',
}

export interface Project {
  id: string
  name: string
  description?: string
  keypoint_schema: string[]
  skeleton_schema: number[][]
  created_at: string
  updated_at: string
  video_path?: string
  video_status: VideoStatus
  video_task_id?: string
  video_uploaded_at?: string
  video_processed_at?: string
  video_error_message?: string
  video_frame_count?: number
  video_extraction_params?: {
    extraction_method: 'uniform' | 'clustering'
    num_frames: number
    start_frame: number
    end_frame: number
  }
}

export interface ImageData {
  id: string
  project_id: string
  filename: string
  storage_path: string
  width: number
  height: number
  frame_number?: number
  meta_data?: {
    source?: 'video_extraction' | 'manual_upload' | 'import_script'
    uploaded_by?: string
    uploaded_at?: string
    timestamp?: number
    extraction_method?: string
  }
  created_at: string
  url?: string // Direct storage URL for accessing the image
}

export type KeypointVisibility = 'not_labeled' | 'inferred' | 'visible'

export interface Keypoint {
  x: number
  y: number
  confidence?: number
  visibility: KeypointVisibility // Three-state visibility: not_labeled, inferred, visible
}

export interface BoundingBox {
  x: number
  y: number
  width: number
  height: number
}

// COCO-like annotation format
export interface COCOAnnotation {
  id: string // Unique annotation ID
  category_id: number // Category ID (default 0 for 'baby')
  bbox: [number, number, number, number] // [x, y, width, height]
  keypoints: number[] // Flattened array [x1, y1, v1, x2, y2, v2, ...] where v is visibility (0=not labeled, 1=labeled but inferred, 2=labeled and visible)
  num_keypoints: number // Number of labeled keypoints
}

export interface ImageAnnotation {
  id: string
  project_id: string
  image_id: string
  user_id: string
  annotations: COCOAnnotation[]
  meta_data: {
    poor_quality?: boolean
    [key: string]: any
  }
  view_settings?: {
    scale?: number
    offset?: { x: number; y: number }
    keypointRadius?: number
    rectangleWidth?: number
    [key: string]: any
  }
  created_at: string
  updated_at: string
}

export interface VideoUploadParams {
  extract_frames?: boolean
  extraction_method?: 'uniform' | 'clustering'
  num_frames?: number
  start_frame?: number
  end_frame?: number
}

// Analysis Types
export interface KeypointConsistency {
  keypoint_index: number
  consistency_score: number
  annotation_count: number
}

export interface AnnotationAnalysis {
  image_id: string
  annotation_count: number
  annotator_count: number
  keypoint_consistency: KeypointConsistency[]
  bbox_consistency: number
  inter_annotator_agreement: number
}

export interface KeypointInferenceStats {
  keypoint_index: number
  keypoint_name?: string
  not_labeled_count: number
  inferred_count: number
  visible_count: number
  inferred_percentage: number
  visible_percentage: number
}

export interface AnnotationStatistics {
  mean_keypoints_per_image: number
  std_keypoints_per_image: number
  mean_bbox_size: number
  std_bbox_size: number
  keypoint_visibility_stats: { [key: number]: number }
  keypoint_inference_stats?: KeypointInferenceStats[]
  overall_inference_rate?: number
}

export interface Annotator {
  id: string
  name: string
  email: string
}

export interface AnnotationConsistencyMetrics {
  annotation_id: number
  image_id: string
  bbox_std: number // Standard deviation of bbox across annotators (as percentage)
  visibility_metric: number // Mean visibility loss across keypoints
  keypoint_std: number // Standard deviation of keypoints (as percentage)
  annotator_count?: number
  object_index?: number // Index of this object within the image (0-based)
  total_objects_in_image?: number // Total number of objects in this image
}

export interface ProjectAnalysis {
  project_id: string
  total_images: number
  annotated_images: number
  total_annotations: number
  annotators: Annotator[]
  annotation_statistics: AnnotationStatistics
  consistency_metrics: AnnotationConsistencyMetrics[]
  mean_bbox_std: number
  mean_visibility_metric: number
  mean_keypoint_std: number
}

export interface ImageWithAnnotations {
  image_id: string
  image_url: string
  width: number
  height: number
  annotations: Array<{
    id: string
    user_id: string
    user_name: string
    user_email: string
    annotations: COCOAnnotation[] // COCO format annotations
    meta_data: any
    created_at?: string
    updated_at?: string
  }>
  merged_annotations: Array<{
    keypoints: number[] // Flattened keypoints array
    bbox: number[] // [x, y, width, height]
    merge_strategy: string
    image_id?: string
    source_annotation_ids?: string[]
    annotator_count?: number
  }>
}

export interface AnnotatorStatistics {
  [userId: string]: {
    name: string
    email: string
    annotation_count: number
    image_count: number
    total_keypoints: number
    visible_keypoints: number
    keypoint_visibility_rate: number
  }
}

// Export Types
export enum ExportFormat {
  COCO = 'coco',
  CSV = 'csv',
  JSON = 'json',
  YOLO = 'yolo',
}

export interface ExportRequest {
  format: ExportFormat
  include_images: boolean
  annotator_ids?: string[]
  merge_annotations: boolean
  merge_strategy: 'average' | 'majority' | 'best_quality'
}

// User-Project Association Types
export interface UserProject {
  id: string
  user_id: string
  project_id: string
  status: ProjectStatus
  is_completed: boolean
  created_at: string
  updated_at: string
}

export interface UserProjectWithTransitions extends UserProject {
  available_transitions: ProjectStatus[]
}

export interface UserProjectStatusTransition {
  target_status: ProjectStatus
  force?: boolean
}

export interface UserProjectWithDetails extends UserProject {
  user?: {
    id: string
    email: string
    name: string
  }
  project?: {
    id: string
    name: string
    description?: string
  }
}

// Extended Project Type with Statistics
export interface ProjectWithStats extends Project {
  total_images: number
  annotated_images_by_user: number
  user_status?: UserProjectWithDetails
  completed_by_count?: number // Admin only - number of users who marked as completed
  qa_pending_user_count?: number // Admin only - number of users currently in qa_pending
  is_user_completed?: boolean
  is_qualified?: boolean
}

// Paginated Projects Response
export interface ProjectsResponse {
  items: ProjectWithStats[]
  total: number
  page: number
  page_size: number
  total_pages: number
}
